import { Menu as BaseMenu } from '@base-ui-components/react/menu';
import { cn } from '../cn.js';
import { IconCheck } from '../icons.js';

/**
 * A menu of actions, opened from a control.
 *
 * What a table's row actions are made of, and the reason that column can hold six things without
 * being six buttons wide. Base UI owns the keyboard — arrows, typeahead, escape, focus return to
 * the trigger — and the positioning; this owns how it looks.
 *
 * **A menu holds actions, not navigation and not state.** Links belong in navigation, and a
 * setting that is on or off is a switch: a menu item that silently toggles is one a person has to
 * open the menu again to read. The one exception is a **radio group**, which shows its current
 * choice with a check every time the menu opens — a preference picked from three, like the theme,
 * is read and changed in the same place without a full-width select in the header for each.
 */
export const DropdownMenu = BaseMenu.Root;
export const DropdownMenuTrigger = BaseMenu.Trigger;

export function DropdownMenuContent({
  className,
  children,
  align = 'end',
  sideOffset = 4,
  ...props
}: BaseMenu.Popup.Props & { align?: 'start' | 'center' | 'end'; sideOffset?: number }) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        align={align}
        sideOffset={sideOffset}
        className="z-(--elevation-dropdown)"
      >
        <BaseMenu.Popup
          className={cn(
            'min-w-(--width-menu) rounded-(--radius-md) p-(--space-xs)',
            'border border-(--color-border) bg-(--color-surface-elevated)',
            'shadow-(--shadow-popover)',
            'transition-opacity duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
            'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            'motion-reduce:transition-none',
            className,
          )}
          {...props}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

export function DropdownMenuItem({
  className,
  destructive = false,
  ...props
}: BaseMenu.Item.Props & { destructive?: boolean }) {
  return (
    <BaseMenu.Item
      className={cn(
        'flex cursor-pointer items-center gap-(--space-sm)',
        'rounded-(--radius-sm) px-(--space-sm) py-(--space-xs)',
        'text-(length:--font-size-body)',
        'outline-none select-none',
        destructive
          ? 'text-(--color-danger) data-[highlighted]:bg-(--color-danger-subtle)'
          : 'text-(--color-text-primary) data-[highlighted]:bg-(--color-accent-subtle) data-[highlighted]:text-(--color-accent)',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-(--opacity-disabled)',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({ className, ...props }: BaseMenu.Separator.Props) {
  return (
    <BaseMenu.Separator
      className={cn('my-(--space-xs) h-px bg-(--color-border)', className)}
      {...props}
    />
  );
}

export function DropdownMenuGroupLabel({ className, ...props }: BaseMenu.GroupLabel.Props) {
  return (
    <BaseMenu.GroupLabel
      className={cn(
        'px-(--space-sm) py-(--space-xs)',
        'text-(length:--font-size-caption) text-(--color-text-muted)',
        className,
      )}
      {...props}
    />
  );
}

export const DropdownMenuGroup = BaseMenu.Group;

/** One choice among several, read and changed in the same place. */
export const DropdownMenuRadioGroup = BaseMenu.RadioGroup;

export function DropdownMenuRadioItem({ className, children, ...props }: BaseMenu.RadioItem.Props) {
  return (
    <BaseMenu.RadioItem
      className={cn(
        'flex cursor-pointer items-center justify-between gap-(--space-sm)',
        'rounded-(--radius-sm) px-(--space-sm) py-(--space-xs)',
        'text-(length:--font-size-body) text-(--color-text-primary)',
        'outline-none select-none',
        'data-[highlighted]:bg-(--color-accent-subtle) data-[highlighted]:text-(--color-accent)',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-(--opacity-disabled)',
        className,
      )}
      {...props}
    >
      {children}
      <BaseMenu.RadioItemIndicator>
        <IconCheck aria-hidden="true" className="size-(--size-icon-sm)" />
      </BaseMenu.RadioItemIndicator>
    </BaseMenu.RadioItem>
  );
}
